import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";
import { PAYMENT_MODE_OPTIONS } from "@/lib/pricingUtils";

/** Card de Ajustes: modo de cobro general al reservar online (sin pago,
 * seña o pago total). Cada servicio puede definir una excepción propia. */
export default function PublicPaymentSettingsCard({ clinic, onUpdated }) {
  const [mode, setMode] = useState(clinic.public_payment_mode || "none");
  const [depositType, setDepositType] = useState(clinic.public_deposit_type || "percentage");
  const [depositValue, setDepositValue] = useState(clinic.public_deposit_value ?? 50);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", {
        public_payment_mode: mode,
        public_deposit_type: depositType,
        public_deposit_value: Number(depositValue) || 0,
      });
      onUpdated(data);
      toast({ title: "Configuración de pago guardada" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
      <div>
        <h2 className="font-serif text-xl text-foreground">Pago en la reserva pública</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Definí si se pide pago al reservar online. Podés hacer excepciones por servicio desde el catálogo de Servicios.
        </p>
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 block">Modo de cobro general</Label>
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid={CLINIC.publicPaymentModeSelect}><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAYMENT_MODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {mode === "deposit" && (
        <div className="flex items-center gap-3">
          <Select value={depositType} onValueChange={setDepositType}>
            <SelectTrigger className="w-44" data-testid={CLINIC.publicDepositTypeSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percentage">Porcentaje (%)</SelectItem>
              <SelectItem value="fixed">Monto fijo (USD)</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min="0" step="0.01" className="w-32" value={depositValue} onChange={(e) => setDepositValue(e.target.value)} data-testid={CLINIC.publicDepositValueInput} />
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Los cobros se generan como links de pago de Square. Mientras Square esté en modo sandbox, los links no cobran dinero real.
      </p>

      <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.publicPaymentSaveButton}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
}
